/**
 * predictive-search.js
 * The <predictive-search> custom element used inside the header's search
 * dialog (snippets/search-modal.liquid).
 *
 *   - Debounced query against Shopify's /search/suggest section-rendering
 *     endpoint, plus an optional metaobject endpoint for editorial content.
 *   - Results are grouped (products / content) and exposed
 *     as an ARIA listbox with roving aria-selected, so the input keeps focus
 *     while the arrow keys walk the options.
 *   - formatMetaobjectType() maps raw metaobject handles to display headings.
 *
 * Extracted verbatim from sections/header.liquid's {% javascript %} block so
 * eslint can see it and so the element no longer depends on the header being
 * present. Self-registering; no init call and no shopify:section:load handler
 * needed — custom elements upgrade themselves whenever markup is re-rendered.
 */
(function () {
  'use strict';
  class PredictiveSearch extends HTMLElement {
    constructor() {
      super();
      this.debounceDelay = 200;
      this.controller = null;
      this.debounceTimer = null;
      this.isKeyboardNav = false;
      // Explicit: setSelected() and the arrow-key handlers read this before any
      // fetch has run, and 'undefined' silently defeats the bounds arithmetic.
      this.currentIndex = -1;
    }

    connectedCallback() {
      this.input = this.querySelector('[data-predictive-search-input]');
      this.reset = this.querySelector('[data-predictive-search-reset]');
      this.results = this.querySelector('[data-predictive-search-container]');
      this.sectionId = this.dataset.sectionId || 'predictive-search';

      this.metaobjectEndpoint = this.dataset.metaobjectEndpoint || '';
      this.metaobjectLimit = parseInt(this.dataset.metaobjectLimit, 10) || 5;
      this.metaobjectLabel = this.dataset.metaobjectLabel || 'Articles & guides';
      try {
        this.metaobjectTypes = this.dataset.metaobjectTypes
          ? JSON.parse(this.dataset.metaobjectTypes)
          : [];
      } catch {
        this.metaobjectTypes = [];
      }

      if (!this.input || !this.results) return;

      this.input.addEventListener('input', this.onInput.bind(this));
      this.input.addEventListener('keydown', this.onKeyDown.bind(this));
      this.reset?.addEventListener('click', this.onReset.bind(this));
      this.results.addEventListener('pointerover', this.onPointer.bind(this));
    }

    disconnectedCallback() {
      this.controller?.abort();
      clearTimeout(this.debounceTimer);
    }

    onInput() {
      const term = this.input.value.trim();
      this.toggleReset(term.length > 0);

      clearTimeout(this.debounceTimer);

      if (!term.length) {
        this.clearResults();
        return;
      }

      this.debounceTimer = setTimeout(() => this.fetchResults(term), this.debounceDelay);
    }

    async fetchResults(term) {
      this.controller?.abort();
      this.controller = new AbortController();
      const { signal } = this.controller;

      // Kick off both requests in parallel. Metaobject may resolve before
      // or after the Shopify predictive search response.
      const shopifyPromise = this.fetchShopifyResults(term, signal);
      const metaobjectPromise = this.metaobjectEndpoint
        ? this.fetchMetaobjectResults(term, signal)
        : Promise.resolve(null);

      try {
        const shopifyHtml = await shopifyPromise;
        if (signal.aborted) return;

        if (shopifyHtml !== null) {
          this.results.innerHTML = shopifyHtml;
        }
        this.input.setAttribute('aria-expanded', 'true');
        this.currentIndex = -1;
      } catch (err) {
        if (err.name === 'AbortError') return;
        console.warn('Predictive search fetch failed:', err);
      }

      // Append metaobject results after Shopify has rendered
      try {
        const items = await metaobjectPromise;
        if (signal.aborted) return;
        if (items?.length) this.appendMetaobjectResults(items, term);
      } catch (err) {
        if (err.name === 'AbortError') return;
        console.warn('Metaobject search fetch failed:', err);
      }
    }

    async fetchShopifyResults(term, signal) {
      const url = new URL('/search/suggest', window.location.origin);
      url.searchParams.set('q', term);
      url.searchParams.set('section_id', this.sectionId);
      // `query` is deliberately absent: the Suggestions pill row was dropped so
      // the panel fits without scrolling. Asking for the types we render keeps
      // Shopify from computing suggestions we throw away.
      url.searchParams.set('resources[type]', 'product,collection,page,article');
      // 3 per type, matching the per-group cap in sections/predictive-search.liquid.
      // The section still caps the combined content group, since this limit is
      // per resource type and collections + pages + articles share one group.
      url.searchParams.set('resources[limit]', '3');
      url.searchParams.set('resources[limit_scope]', 'each');

      const response = await fetch(url.toString(), { signal });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const text = await response.text();
      // Shopify wraps the section markup in a <div class="shopify-section">.
      const parsed = new DOMParser().parseFromString(text, 'text/html');
      return parsed.querySelector('.shopify-section')?.innerHTML ?? text;
    }

    async fetchMetaobjectResults(term, signal) {
      if (!this.metaobjectEndpoint) return null;

      const hostname = window.location.hostname;
      let environment = 'production';
      if (
        hostname.includes('staging') ||
        (hostname.endsWith('.myshopify.com') && !hostname.includes('sleepdoctor'))
      ) {
        environment = 'staging';
      }

      const response = await fetch(this.metaobjectEndpoint, {
        method: 'POST',
        signal,
        headers: {
          'Content-Type': 'application/json',
          'X-Shop-Environment': environment,
        },
        body: JSON.stringify({
          query: term,
          limit: this.metaobjectLimit,
          cursor: null,
          types: this.metaobjectTypes,
        }),
      });

      if (!response.ok) throw new Error(`Metaobject HTTP ${response.status}`);
      const data = await response.json();
      return Array.isArray(data?.items) ? data.items : [];
    }

    appendMetaobjectResults(items, _term) {
      const listbox = this.results.querySelector('#predictive-search-results');
      if (!listbox) return;

      // Remove any stale metaobject group from a previous render
      listbox.querySelector('[data-metaobject-group]')?.remove();

      const section = document.createElement('section');
      section.className = 'predictive-search-results__group';
      section.setAttribute('aria-labelledby', 'predictive-search-metaobject-heading');
      section.setAttribute('data-metaobject-group', '');

      const heading = document.createElement('h2');
      heading.id = 'predictive-search-metaobject-heading';
      heading.className = 'predictive-search-results__heading';
      heading.textContent = this.metaobjectLabel || 'Articles & guides';
      section.appendChild(heading);

      const list = document.createElement('ul');
      list.className = 'predictive-search-results__content';
      list.setAttribute('role', 'list');

      items.forEach((item) => {
        const li = document.createElement('li');
        li.className = 'predictive-search-results__content-item';
        li.setAttribute('role', 'option');
        li.setAttribute('aria-selected', 'false');

        const anchor = document.createElement('a');
        anchor.href = item.url || '#';
        anchor.className = 'predictive-search-results__content-link';
        anchor.tabIndex = -1;

        if (item.type) {
          const label = document.createElement('span');
          label.className = 'predictive-search-results__content-label';
          label.textContent = this.formatMetaobjectType(item.type);
          anchor.appendChild(label);
        }

        const title = document.createElement('span');
        title.className = 'predictive-search-results__content-title';
        title.textContent = item.title || '';
        anchor.appendChild(title);

        li.appendChild(anchor);
        list.appendChild(li);
      });

      section.appendChild(list);

      // Insert before the "View all" link so it stays the last element
      const viewAll = listbox.querySelector('.predictive-search-results__view-all');
      if (viewAll) {
        listbox.insertBefore(section, viewAll);
      } else {
        listbox.appendChild(section);
      }
    }

    formatMetaobjectType(type) {
      if (!type) return '';
      const map = {
        chronotype_articles: 'Chronotypes',
        cpap_articles: 'CPAP',
        health_articles: 'Health',
        insomnia_articles: 'Insomnia',
        sleep_aids_articles: 'Sleep Aids',
        sleep_apnea_articles: 'Sleep Apnea',
        sleep_doctor_commercial: 'Reviews',
        snoring_articles: 'Snoring',
      };
      if (map[type]) return map[type];
      return type.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
    }

    clearResults() {
      this.controller?.abort();
      this.results.innerHTML = '';
      this.input.setAttribute('aria-expanded', 'false');
      this.currentIndex = -1;
    }

    onReset(event) {
      event.preventDefault();
      this.input.value = '';
      this.toggleReset(false);
      this.clearResults();
      this.input.focus();
    }

    toggleReset(show) {
      if (!this.reset) return;
      this.reset.hidden = !show;
    }

    get allItems() {
      return Array.from(this.results.querySelectorAll('[role="option"]'));
    }

    setSelected(index) {
      const items = this.allItems;
      if (!items.length) {
        this.currentIndex = -1;
        return;
      }
      const bounded = (index + items.length) % items.length;
      items.forEach((item, i) => {
        const match = i === bounded;
        item.setAttribute('aria-selected', match ? 'true' : 'false');
        if (match) item.scrollIntoView({ block: 'nearest' });
      });
      this.currentIndex = bounded;
    }

    onKeyDown(event) {
      const items = this.allItems;

      if (event.key === 'Escape') {
        if (this.input.value) {
          this.input.value = '';
          this.toggleReset(false);
          this.clearResults();
        } else {
          this.closest('dialog')?.close();
        }
        return;
      }

      if (event.key === 'ArrowDown') {
        if (!items.length) return;
        event.preventDefault();
        this.isKeyboardNav = true;
        this.setSelected((this.currentIndex ?? -1) + 1);
        return;
      }

      if (event.key === 'ArrowUp') {
        if (!items.length) return;
        event.preventDefault();
        this.isKeyboardNav = true;
        this.setSelected((this.currentIndex ?? 0) - 1);
        return;
      }

      if (event.key === 'Enter') {
        const active = items[this.currentIndex];
        if (active) {
          event.preventDefault();
          active.querySelector('a')?.click();
        }
        // else: form submits naturally to /search?q=...
      }
    }

    onPointer(event) {
      this.isKeyboardNav = false;
      // Explicit: setSelected() and the arrow-key handlers read this before any
      // fetch has run, and 'undefined' silently defeats the bounds arithmetic.
      this.currentIndex = -1;
      const option = event.target.closest('[role="option"]');
      if (!option) return;
      const items = this.allItems;
      const idx = items.indexOf(option);
      if (idx !== -1) this.setSelected(idx);
    }
  }

  if (!customElements.get('predictive-search')) {
    customElements.define('predictive-search', PredictiveSearch);
  }
})();
